import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import type { TouchpointSourceType } from '@trello/ad-sequencer';
import { isDesktop } from '@trello/browser';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import type { ExperimentVariations } from '@trello/feature-gate-client';
import { useGetExperimentValue } from '@trello/feature-gate-client';
import { useHasValidAaSession } from '@trello/heartbeat/session';

import { getAvailableSites } from '../getAvailableSites';
import {
  type TrelloCrossFlowExperiment,
  trelloCrossFlowOpen,
} from '../TrelloCrossFlow';
import { isSourceTargetedByFeatureKey } from './isSourceTargetedByFeatureFlag';
import { useNonPpmFreeMultiplayerFragment } from './NonPpmFreeMultiplayerFragment.generated';
import { useEligibleWorkspacesForProvisioning } from './useEligibleWorkspacesForProvisioning';

const oneTimeMessageSource: Partial<Record<TouchpointSourceType, string>> = {
  boardScreen: '1p-ads-non-ppm-single-user-tool-survey',
};
const experimentFeatureKey =
  'trello_xf_non_ppm_free_multiplayer_collapsible_ad';

export type NonPpmFreeMultiplayerExperimentVariations = ExperimentVariations<
  typeof experimentFeatureKey,
  'cohort'
>;

export interface CrossFlowForFunnelExperimentParams {
  source: TouchpointSourceType;
  workspaceId?: string;
  boardId?: string;
}

export const useCrossFlowForNonPpmFreeMultiplayer = ({
  source,
  workspaceId,
  boardId,
}: CrossFlowForFunnelExperimentParams): TrelloCrossFlowExperiment<NonPpmFreeMultiplayerExperimentVariations> => {
  const crossFlow = useCrossFlow();

  const [hasValidAaSession] = useHasValidAaSession();
  const [loadingAvailableSites, setLoadingAvailableSites] = useState(false);
  const [siteEligibility, setSiteEligibility] = useState(false);
  const { data: boardData } = useNonPpmFreeMultiplayerFragment({
    from: { id: boardId },
    optimistic: false,
  });

  const workspaceMembers = boardData?.organization?.members;

  // Users is in a workspace with other guests/members
  const isMultiplayerUser =
    (workspaceMembers &&
      workspaceMembers.length > 1 &&
      workspaceMembers.length < 10) ??
    false;

  const { isOneTimeMessageDismissed } = useOneTimeMessagesDismissed();

  const isSourceOneTimeMessageDismissed = useCallback(
    () => isOneTimeMessageDismissed(oneTimeMessageSource?.[source] || ''),
    [source, isOneTimeMessageDismissed],
  );

  const eligibleWorkspaceOptions = useEligibleWorkspacesForProvisioning({
    workspaceId,
    entitlementRequired: 'free',
  });

  const [fireExposureEvent, setFireExposureEvent] = useState(false);

  const { cohort } = useGetExperimentValue({
    experimentName: experimentFeatureKey,
    parameter: 'cohort',
    fireExposureEvent,
  });

  const isRuntimeEligibilityChecksPassed = useMemo(
    () =>
      crossFlow.isEnabled &&
      siteEligibility &&
      eligibleWorkspaceOptions.length > 0 &&
      isMultiplayerUser &&
      !isSourceOneTimeMessageDismissed(),
    [
      crossFlow.isEnabled,
      eligibleWorkspaceOptions.length,
      isMultiplayerUser,
      isSourceOneTimeMessageDismissed,
      siteEligibility,
    ],
  );

  const getIsTargeted = useCallback(() => {
    return cohort !== 'not-enrolled';
  }, [cohort]);

  const getExperimentCohort = useCallback(() => {
    if (isRuntimeEligibilityChecksPassed) {
      setFireExposureEvent(true);
      return cohort;
    }

    return 'not-enrolled';
  }, [cohort, isRuntimeEligibilityChecksPassed]);

  useEffect(() => {
    const loadSiteEligibility = async () => {
      try {
        setLoadingAvailableSites(true);
        const { sites } = await getAvailableSites();
        const hasNoSites =
          sites?.length > 0 &&
          sites?.filter(({ cloudId }) => cloudId !== 'TRELLO').length === 0;
        setSiteEligibility(hasNoSites);
      } finally {
        setLoadingAvailableSites(false);
      }
    };

    if (
      !loadingAvailableSites &&
      crossFlow.isEnabled &&
      !isDesktop() &&
      isMultiplayerUser &&
      getIsTargeted() &&
      isSourceTargetedByFeatureKey({
        featureKey: experimentFeatureKey,
        source,
      })
    ) {
      loadSiteEligibility();
    }

    // Intentionally ignoring loadingAvailableSites dependency
    // eslint-disable-next-line @trello/exhaustive-deps
  }, [
    isSourceOneTimeMessageDismissed,
    source,
    workspaceId,
    isMultiplayerUser,
    crossFlow.isEnabled,
    experimentFeatureKey,
    getIsTargeted,
  ]);

  return useMemo(
    () => ({
      isEnrolled: isRuntimeEligibilityChecksPassed,
      experimentName: experimentFeatureKey,
      getIsTargeted,
      getExperimentCohort,
      open: trelloCrossFlowOpen(
        crossFlow,
        source,
        eligibleWorkspaceOptions,
        hasValidAaSession,
        {
          workspaceId,
          boardId,
        },
      ),
    }),
    [
      boardId,
      crossFlow,
      eligibleWorkspaceOptions,
      getExperimentCohort,
      getIsTargeted,
      hasValidAaSession,
      isRuntimeEligibilityChecksPassed,
      source,
      workspaceId,
    ],
  );
};
